import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import WidgetSkeleton from "@/components/ui/widgetSkeleton";
import {
  formatPeriodCase,
  periodEnumToString
} from "@/entities/reporting/helpers";
import { KPIsReportData, SettingsPeriod } from "@/entities/reporting/types";
import { useFiscalYearStartMonth } from "@/entities/settings/useFiscalYearStartMonth";
import { cn } from "@/utils/utils";
import KPICard from "./KPICard";
import KPIValue from "./KPIValue";
import {
  fiscalQuarter,
  fiscalYearLabel,
  formatFiscalYearStart,
  formatMoney,
  formatPercent,
  formatShortMonth
} from "./format";
import {
  currentRecurringRevenuePopover,
  netNewRevenuePopover,
  newBookingsPopover,
  retentionPopover,
  revenueUnit
} from "./revenuePopovers";

interface Props {
  kpis?: KPIsReportData;
  isLoading?: boolean;
  arrMrrLabel: string;
  className?: string;
}

/** Column spans of the cards, in order, on the six-column grid. */
const SKELETON_SPANS = [2, 2, 2, 2, 1, 1, 2];

/**
 * The revenue KPI bar: recurring revenue, net new revenue, bookings and
 * retention for the fiscal year, beside quota attainment, committed and closing
 * power. Revenue figures the server could not compute render as a dash with no
 * breakdown, and the sales figures still render.
 */
const KPIBarV2 = ({ kpis, isLoading, arrMrrLabel, className }: Props) => {
  const { t } = useTranslation();
  const fiscalYearStartMonth = useFiscalYearStartMonth();
  const fiscalYearStart = useMemo(
    () => formatFiscalYearStart(fiscalYearStartMonth),
    [fiscalYearStartMonth]
  );
  const today = new Date();
  const quarter = fiscalQuarter(today, fiscalYearStartMonth);

  const isQuarter = (period?: number) =>
    period == null || period === SettingsPeriod.QUARTER;
  const periodName = (period?: number) =>
    formatPeriodCase(
      t(`timeframes.${periodEnumToString(period ?? SettingsPeriod.QUARTER)}`)
    );

  const revenue = kpis?.revenue;
  const crr = revenue?.currentRecurringRevenue;
  const netNew = revenue?.netNewRevenue;
  const bookings = revenue?.newBookings;
  const retention = revenue?.retention;
  // Labels follow the unit the report's figures are in, not the setting read here.
  const unit = revenueUnit(t, revenue?.isArr);

  const salesVsQuota = kpis?.salesVsQuota?.[0];
  const isCurrency = salesVsQuota?.metadata?.currency ?? true;
  const sales = salesVsQuota?.value;
  const quota = salesVsQuota?.comparedTo;
  const quotaPeriod = salesVsQuota?.metadata?.period;
  // The server reports a quota of -1 when the user has none.
  const hasQuota = sales != null && quota != null && quota > 0;
  const committed = kpis?.committed?.[0];
  const closingPower = kpis?.closingPower?.[0];
  const closingPowerPeriod = closingPower?.metadata?.period;

  const content = isLoading ? (
    SKELETON_SPANS.map((span, index) => (
      <WidgetSkeleton
        key={index}
        className={cn(
          "h-[124px] rounded-2xl",
          span === 2 ? "col-span-2" : "col-span-1"
        )}
      />
    ))
  ) : (
    <>
      <KPICard
        dataTest="kpi-current-recurring-revenue"
        className="col-span-2"
        title={t("kpisV2.ytdTitle", { title: t("kpisV2.currentRecurringRevenue") })}
        info={t("kpisV2.tooltip.currentRecurringRevenue")}
        popover={
          revenue
            ? currentRecurringRevenuePopover(t, revenue, fiscalYearStart, unit)
            : undefined
        }
      >
        <KPIValue
          dataTest="kpi-crr-arr"
          value={formatMoney(crr?.arr)}
          label={unit.arr}
          yoy={crr?.arrYoy}
        />
        <KPIValue
          dataTest="kpi-crr-carr"
          value={formatMoney(crr?.carrFyExit)}
          label={t("kpisV2.carrFyExit", unit)}
          yoy={crr?.carrYoy}
        />
      </KPICard>
      <KPICard
        dataTest="kpi-net-new-revenue"
        className="col-span-2"
        title={t("kpisV2.ytdTitle", { title: t("kpisV2.netNewRevenue") })}
        info={t("kpisV2.tooltip.netNewRevenue")}
        popover={revenue ? netNewRevenuePopover(t, revenue, unit) : undefined}
      >
        <KPIValue
          dataTest="kpi-net-new-arr"
          value={formatMoney(netNew?.arr, { signed: true })}
          label={unit.arr}
          yoy={netNew?.arrYoy}
        />
        <KPIValue
          dataTest="kpi-net-new-carr"
          value={formatMoney(netNew?.carr, { signed: true })}
          label={t("kpisV2.carrFyExit", unit)}
          yoy={netNew?.carrYoy}
        />
      </KPICard>
      <KPICard
        dataTest="kpi-new-bookings"
        className="col-span-2"
        title={t("kpisV2.ytdTitle", { title: t("kpisV2.newBookings") })}
        info={t("kpisV2.tooltip.newBookings")}
        popover={revenue ? newBookingsPopover(t, revenue) : undefined}
      >
        <KPIValue
          dataTest="kpi-bookings-acv"
          value={formatMoney(bookings?.acv)}
          label={t("kpisV2.acv")}
          yoy={bookings?.acvYoy}
        />
        <KPIValue
          dataTest="kpi-bookings-tcv"
          value={formatMoney(bookings?.tcv)}
          label={t("kpisV2.tcv")}
          yoy={bookings?.tcvYoy}
        />
      </KPICard>
      <KPICard
        dataTest="kpi-retention"
        className="col-span-2"
        title={t("kpisV2.ytdTitle", { title: t("kpisV2.retention") })}
        info={t("kpisV2.tooltip.retention")}
        popover={
          retention ? retentionPopover(t, retention, fiscalYearStart, unit) : undefined
        }
      >
        <KPIValue
          dataTest="kpi-retention-grr"
          value={formatPercent(retention?.grr)}
          label={t("kpis.grr")}
        />
        <KPIValue
          dataTest="kpi-retention-nrr"
          value={formatPercent(retention?.nrr)}
          label={t("kpis.nrr")}
        />
      </KPICard>
      <KPICard
        dataTest="kpi-quota-attainment"
        className="col-span-1"
        title={
          isQuarter(quotaPeriod)
            ? t("kpisV2.quotaAttainmentQuarter", { quarter })
            : quotaPeriod === SettingsPeriod.YEAR
              ? t("kpisV2.quotaAttainmentNamed", {
                  name: fiscalYearLabel(today, fiscalYearStartMonth)
                })
              : quotaPeriod === SettingsPeriod.MONTH
                ? t("kpisV2.quotaAttainmentNamed", {
                    name: formatShortMonth(today)
                  })
                : t("kpisV2.quotaAttainment", {
                    period: periodName(quotaPeriod)
                  })
        }
        info={t("kpisV2.tooltip.quotaAttainment")}
      >
        {/* Without a quota the card shows what was sold, as the previous bar did. */}
        <KPIValue
          dataTest="kpi-quota-attainment-figure"
          value={
            hasQuota
              ? formatPercent((sales / quota) * 100)
              : formatMoney(sales, { isCurrency })
          }
          label={
            hasQuota
              ? `${formatMoney(sales, { isCurrency })} / ${formatMoney(quota, { isCurrency })}`
              : undefined
          }
          compactLabel
        />
      </KPICard>
      <KPICard
        dataTest="kpi-committed"
        className="col-span-1"
        title={t("kpisV2.committed", { arrMrr: arrMrrLabel })}
        info={t("kpisV2.tooltip.committed", { arrMrr: arrMrrLabel })}
      >
        <KPIValue
          dataTest="kpi-committed-figure"
          value={formatMoney(committed?.value, {
            isCurrency: committed?.metadata?.currency ?? true
          })}
          label={
            hasQuota
              ? t("kpisV2.remainingQuota", {
                  amount: formatMoney(Math.max(quota - sales, 0), { isCurrency })
                })
              : undefined
          }
          compactLabel
        />
      </KPICard>
      <KPICard
        dataTest="kpi-closing-power"
        className="col-span-2"
        title={
          isQuarter(closingPowerPeriod)
            ? t("kpisV2.closingPowerQuarter", { quarter })
            : t("kpis.closingPower", { period: periodName(closingPowerPeriod) })
        }
        info={t("kpis.tooltip.closingPower", {
          period: periodName(closingPowerPeriod)
        })}
      >
        <KPIValue
          dataTest="kpi-closing-power-figure"
          value={formatMoney(closingPower?.value, {
            isCurrency: closingPower?.metadata?.currency ?? true
          })}
          label={arrMrrLabel}
        />
      </KPICard>
    </>
  );

  return (
    <div className="flex flex-row justify-center" data-test="kpi-bar-v2-container">
      <div className="grow-1" />
      <div
        data-test="kpi-bar-v2"
        className={cn(
          "grid max-w-[1700px] grow-[2] grid-cols-6 gap-4 bg-surface-elevation0 px-0 py-4",
          className
        )}
      >
        {content}
      </div>
      <div className="grow-1" />
    </div>
  );
};

export default KPIBarV2;
